import { Flex } from '@chakra-ui/core'
import { IComment } from 'constants/interfaces'
import React, { memo, useCallback, useEffect, useRef, useState } from 'react'
import { ChatItem } from '../ChatItem'
import { getCurrentEpoch } from 'utils'
import { IFetchComments } from 'hooks/useComments'
import { Post } from 'redux/reducers/feed'
import InfiniteScroll from 'react-infinite-scroll-component'
import { Loader } from 'components/CommonComponents/Loader'
import { useUniqueMessages } from 'hooks/useUniqueMessages'
import { setViewedCommentsIds, updateViews } from 'redux/actions/comments'
import { useDispatch } from 'react-redux'

interface ChatModalBodyProps {
  currentChat: Post
  fetchCommentsLoading: boolean
  chatId: string
  fetchComments: (body: IFetchComments) => Promise<IComment[] | undefined>
  comments: IComment[]
  commentsCounter: number
  messages:
    | {
        items: IComment[]
        hasMore: boolean
      }
    | undefined
  onMessageClick?: (commentId: string) => void
  targetCommentId?: string | null
}

export const ChatModalBody = memo(
  ({
    currentChat,
    messages,
    comments,
    fetchComments,
    chatId,
    fetchCommentsLoading,
    onMessageClick,
    targetCommentId,
  }: ChatModalBodyProps) => {
    const dispatch = useDispatch()
    const scrollableDivRef = useRef<HTMLDivElement>(null)
    const [flexDirection, setFlexDirection] = useState<'column' | 'column-reverse'>('column-reverse')
    const [lastMessageCreatedAt, setLastMessageCreatedAt] = useState<number | null>(null)
    const uniqueMessages = useUniqueMessages(messages?.items)

    const getComments = useCallback(() => {
      if (fetchCommentsLoading) return

      try {
        let timestampFromToGetComments = getCurrentEpoch()

        if (comments.length > 0) {
          timestampFromToGetComments = comments[0].created_at_ts
        }

        fetchComments({
          limit: 12,
          created_ts: timestampFromToGetComments,
          isManuallyTriggered: true,
          id: chatId,
        })
      } catch (error) {
        console.log(error)
      }
    }, [chatId, comments, fetchComments, fetchCommentsLoading])

    useEffect(() => {
      if (scrollableDivRef.current) {
        const container = scrollableDivRef.current
        const shouldUseColumn = container.scrollHeight <= container.clientHeight
        setFlexDirection(shouldUseColumn ? 'column' : 'column-reverse')
      }
    }, [messages])

    useEffect(() => {
      if (messages?.items.length && messages.items.length > 0) {
        const lastMessage = messages.items[messages.items.length - 1]
        setLastMessageCreatedAt(lastMessage.created_at_ts)
      }
    }, [messages])

    useEffect(() => {
      if (scrollableDivRef.current && lastMessageCreatedAt) {
        const container = scrollableDivRef.current
        const isAtBottom = container.scrollHeight - container.scrollTop <= container.clientHeight + 100

        if (isAtBottom) {
          container.scrollTop = container.scrollHeight
        }
      }
    }, [lastMessageCreatedAt])

    useEffect(
      () => {
        if (currentChat) {
          const idsPayload = comments?.map((item) => item.cid) || []
          dispatch(
            updateViews({
              postID: currentChat.post_id,
              viewedIds: idsPayload,
            })
          )

          dispatch(
            setViewedCommentsIds({
              key: currentChat.post_id,
              ids: idsPayload,
            })
          )
        }
      },

      // eslint-disable-next-line react-hooks/exhaustive-deps
      [dispatch, currentChat]
    )

    return (
      <Flex
        id="chatModalScrollContainer"
        ref={scrollableDivRef}
        flexDir={flexDirection}
        h="100%"
        w="100%"
        overflowY="auto"
        p="20px"
        dir="ltr"
      >
        <InfiniteScroll
          dataLength={messages?.items.length ?? 0}
          next={getComments}
          style={{ display: 'flex', flexDirection: 'column-reverse' }}
          inverse={true}
          hasMore={messages?.hasMore || false}
          loader={<Loader maxWidth="100%" />}
          scrollableTarget="chatModalScrollContainer"
        >
          {uniqueMessages?.map((message: IComment) => (
            <ChatItem
              key={message.cid}
              message={message}
              currentChat={currentChat}
              onMessageClick={onMessageClick}
              isTarget={targetCommentId === message.cid}
            />
          ))}
        </InfiniteScroll>
      </Flex>
    )
  }
)
